import React, { ReactElement, useEffect, useState } from 'react';
import { Input, MenuProps, SelectProps, Space } from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { queryKeysClient } from 'common/clients/query-key.client';
import { queryClient } from 'common/clients/query.client';
import { Select } from 'common/components/antd/Select/Select';
import { Table } from 'common/components/antd/Table/Table';
import { ENV_COUNTERPARTY_GUID } from 'common/constants/env.constants';
import { DEFAULT_PAGE, DEFAULT_PAGE_SIZE } from 'common/constants/table.constants';
import { EQueryKeys } from 'common/enums/query-kyes.enum';
import { useGetCounterpartiesQuery } from 'domains/counterparties/queries/counterparty.query';
import { TCounterpartiesListReqParams } from 'domains/counterparties/types/counterparty.types';
import {
	useDeleteMerchantMutation,
	useGetMerchantsListQuery,
} from 'domains/merchant/queries/merchant.query';
import { TGetMerchantsListResData } from 'domains/merchant/types/merchant.types';
import { ROUTES_MAPPING } from 'navigation/constants/route.constants';
import {
  EMerchantAction,
  EMerchantsTableColumnKey,
  EModalType,
  EUserManagementMerchantsPageGetParams,
  EUserManagementMerchantsPageGetParamsActionTypes,
} from './enums/merchants.enums';
import { SDangerActionItem } from './merchants.page.style';
import CreateEditMerchantsModal from './modals/create-edit-merchants.modal';
import { getColumns } from './table/merchants-table.columns';
import { TMerchantModalData, TMerchantsTableRecord, TTablePaginationConfig } from './types/merchants.types';

const UserRoleManagementMerchantPage = (): ReactElement => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [selectedCounterparty, setSelectedCounterparty] = useState(ENV_COUNTERPARTY_GUID);
  const [modalData, setModalData] = useState<TMerchantModalData | {}>({});
  const [paginationData, setPaginationData] = useState<TTablePaginationConfig>({
    page: DEFAULT_PAGE,
    pageSize: DEFAULT_PAGE_SIZE,
  });

  const {
    data: counterpartiesData,
    isError: isCounterpartiesDataError,
    error: counterpartiesDataError,
  } = useGetCounterpartiesQuery(
    {},
    {
      refetchOnMount: false,
    },
  );

  const {
    data: merchantsListData,
    isLoading: isMerchantsListDataLoading,
    isError: isMerchantsListDataError,
    error: merchantsListDataError,
  } = useGetMerchantsListQuery(
    ENV_COUNTERPARTY_GUID,
    {
      limit: paginationData.pageSize,
      offset: (paginationData.page - 1) * paginationData.pageSize,
    },
    {
      refetchOnMount: false,
    },
  );

  const isError = isMerchantsListDataError || isCounterpartiesDataError;
  const error = merchantsListDataError || counterpartiesDataError;

  useEffect(() => {
    if (isError) {
      console.error(error);
    }
  }, [isError, error]);

  useEffect(() => {
    const searchParams = new URLSearchParams(document.location.search);
    const pageAction = searchParams.get(EUserManagementMerchantsPageGetParams.Action);

    if (pageAction === EUserManagementMerchantsPageGetParamsActionTypes.Create) {
      setModalData({
        type: EModalType.Create,
        rowData: {},
        counterpartiesData,
      });
    }
  }, [document.location.search, counterpartiesData]);

  const { mutateAsync: deleteMerchantMutation } = useDeleteMerchantMutation();

  const actions: MenuProps['items'] = [
    {
      key: EMerchantAction.Edit,
      label: t('usrRoleMng.merchants.table.rowAction.edit'),
    },
    {
      key: EMerchantAction.Delete,
      label: <SDangerActionItem>{t('usrRoleMng.merchants.table.rowAction.delete')}</SDangerActionItem>,
    },
  ];

  const onItemActionClick = async ({ record, actionKey }: {
    record: TMerchantsTableRecord,
    actionKey: string,
  }): Promise<void> => {
    try {
      switch (actionKey) {
        case EMerchantAction.Edit: {
          setModalData({
            type: EModalType.Edit,
            rowData: record,
            counterpartiesData,
          });
          break;
        }
        case EMerchantAction.Delete: {
          await deleteMerchantMutation({ merchantGUID: record.customer_guid });
          await queryClient.invalidateQueries({
            queryKey: queryKeysClient[EQueryKeys.MerchantQueryKeys].getMerchantsList._def,
          });
          break;
        }
        default: {
          break;
        }
      }
    } catch (e) {
      console.error(e);
    }
  };

  const counterpartyOptions = (counterpartiesData?.counterparties || []).map((c) => ({
    label: c.name,
    value: c.guid,
  }));

  const selectedCounterpartyOption = counterpartyOptions.find((opt) => opt.value === selectedCounterparty);

  const changeCounterparty: SelectProps['onChange'] = (value: string) => {
    setSelectedCounterparty(value);
  }

  const handlePaginationChange = (pageParam: number, pageSizeParam: number): void => {
    setPaginationData({
      page: pageParam,
      pageSize: pageSizeParam,
    });
  };

  const columns = getColumns({
    menuItems: actions,
    onItemActionClick,
  });

  const onCancelModalHandler = (): void => {
    setModalData({});
    navigate(ROUTES_MAPPING.PROTECTED.USER_ROLE_MANAGEMENT_MERCHANTS);
  };

  return (
    <>
      <Space style={{ padding: '16px' }}>
        <Select
          style={{
	          width: 272,
          }}
          defaultValue={selectedCounterparty}
          value={selectedCounterpartyOption}
          options={counterpartyOptions}
          onChange={changeCounterparty}
        />
	      <Input.Search
	        style={{ width: '340px' }}
	        placeholder={t('usrRoleMng.merchants.filtersRow.searchByGuid.placeholder') as string}
	        // onSearch={handleSearch}
	        allowClear
	      />
      </Space>
      <Table
        pagination={{
          style: {
            marginRight: 30,
          },
          showQuickJumper: true,
          showSizeChanger: true,
          total: merchantsListData?.total || 0,
          pageSize: paginationData.pageSize,
          current: paginationData.page,
          onChange: handlePaginationChange,
        }}
        rowKey={EMerchantsTableColumnKey.GUID}
        dataSource={merchantsListData?.merchants}
        columns={columns}
        loading={isMerchantsListDataLoading}
        onRow={(record): { style: { textDecoration: string } } => ({
          style: {
            textDecoration: record?.deleted_at ? 'line-through' : 'default',
          }
        })}
      />
      <CreateEditMerchantsModal
        isOpen={!!Object.keys(modalData).length}
        onCancelModalHandler={onCancelModalHandler}
        data={modalData as TMerchantModalData}
      />
    </>
  );
};

export const loader = async (): Promise<{
  counterpartiesData: TCounterpartiesListReqParams | {},
  merchantsList: TGetMerchantsListResData | {}
}> => {
  try {
    const counterpartiesDataQuery = queryKeysClient[EQueryKeys.CounterpartyQueryKeys].getCounterpartiesList();
    const counterpartiesData: TCounterpartiesListReqParams =
      queryClient.getQueryData(counterpartiesDataQuery.queryKey) ??
      (await queryClient.fetchQuery(counterpartiesDataQuery));

    const merchantsListQuery = queryKeysClient[EQueryKeys.MerchantQueryKeys].getMerchantsList(
      ENV_COUNTERPARTY_GUID,
      {
        offset: (DEFAULT_PAGE - 1) * DEFAULT_PAGE_SIZE,
        limit: DEFAULT_PAGE_SIZE,
      }
    );
    const merchantsList: TGetMerchantsListResData =
      queryClient.getQueryData(merchantsListQuery.queryKey) ??
      (await queryClient.fetchQuery(merchantsListQuery));

    return {
      counterpartiesData,
      merchantsList,
    };
  } catch (error) {
    console.error(error);

    return {
      counterpartiesData: {},
      merchantsList: {},
    };
  }
};

export default UserRoleManagementMerchantPage;
